<template>
  <svg
    viewBox="0 0 1280 640"
    preserveAspectRatio="xMidYMid slice"
    class="h-full w-full"
    aria-hidden="true"
  >
    <line
      ref="link"
      stroke="#a1a1aa"
      stroke-width="1"
      stroke-dasharray="5 4"
      opacity="0"
    />
    <g
      v-for="f in FISH"
      :key="f.id"
      :data-fish="f.id"
      opacity="0.08"
      fill="#a1a1aa"
      stroke="#a1a1aa"
    >
      <g :transform="`scale(${f.size})`">
        <path
          d="M30 0 C20 -6 10 -16 -6 -20 C-16 -22 -16 -12 -11 -5 C-13 -2 -13 2 -11 5 C-16 12 -16 22 -6 20 C10 16 20 6 30 0 Z"
          stroke="none"
        />
        <path
          d="M-11 0 L-34 0"
          fill="none"
          stroke-width="2.5"
          stroke-linecap="round"
        />
      </g>
    </g>
  </svg>
</template>

<script>
import { gsap } from "gsap";

// Hand-tuned drift routes in viewBox units. Each fish crosses the hero and
// wraps back to its start; waypoints are chosen so routes intersect loosely
// (the ticker below turns close passes into brief "connection" lines).
const FISH = [
  {
    id: 0,
    size: 1,
    dur: 34,
    route: [
      { x: -80, y: 140 },
      { x: 420, y: 220 },
      { x: 880, y: 130 },
      { x: 1370, y: 240 },
    ],
  },
  {
    id: 1,
    size: 0.65,
    dur: 40,
    route: [
      { x: 1370, y: 430 },
      { x: 820, y: 350 },
      { x: 320, y: 470 },
      { x: -80, y: 390 },
    ],
  },
  {
    id: 2,
    size: 0.45,
    dur: 26,
    route: [
      { x: -80, y: 540 },
      { x: 520, y: 580 },
      { x: 1370, y: 490 },
    ],
  },
];

const LINK_RANGE = 190; // viewBox units
const LINK_HOLD = 3; // seconds a connection stays up
const LINK_COOLDOWN = 7; // seconds before the next one may form

export default {
  name: "HeroFish",

  data() {
    return { FISH };
  },

  created() {
    this._tweens = [];
    this._pair = null;
    this._pairEnd = 0;
    this._cooldownUntil = 0;
  },

  mounted() {
    FISH.forEach((f) => this.swim(f));
    this._tick = (time) => this.onTick(time);
    gsap.ticker.add(this._tick);
  },

  beforeUnmount() {
    gsap.ticker.remove(this._tick);
    this._tweens.forEach((t) => t.kill());
  },

  methods: {
    fishEl(id) {
      return this.$el.querySelector(`[data-fish="${id}"]`);
    },
    track(t) {
      this._tweens.push(t);
      return t;
    },

    // Endless drift along the route, nose turned toward each next waypoint.
    // A top-view ray is symmetric, so plain rotation covers both directions.
    swim(f) {
      const el = this.fishEl(f.id);
      const total = f.route
        .slice(1)
        .reduce(
          (sum, p, i) =>
            sum + Math.hypot(p.x - f.route[i].x, p.y - f.route[i].y),
          0
        );
      const start = f.route[0];
      gsap.set(el, { x: start.x, y: start.y, svgOrigin: "0 0" });
      const tl = this.track(
        gsap.timeline({ repeat: -1, defaults: { ease: "none" } })
      );
      f.route.slice(1).forEach((p, i) => {
        const prev = f.route[i];
        const seg = Math.hypot(p.x - prev.x, p.y - prev.y);
        const deg = (Math.atan2(p.y - prev.y, p.x - prev.x) * 180) / Math.PI;
        tl.to(el, { x: p.x, y: p.y, duration: (seg / total) * f.dur });
        tl.to(el, { rotation: deg, duration: Math.min(1.5, seg / 200) }, "<");
      });
    },

    onTick(time) {
      const link = this.$refs.link;
      if (!link) return;
      const pos = FISH.map((f) => {
        const el = this.fishEl(f.id);
        return {
          x: gsap.getProperty(el, "x"),
          y: gsap.getProperty(el, "y"),
        };
      });

      if (this._pair) {
        const [a, b] = this._pair;
        gsap.set(link, {
          attr: { x1: pos[a].x, y1: pos[a].y, x2: pos[b].x, y2: pos[b].y },
        });
        if (time > this._pairEnd) {
          this._pair = null;
          this._cooldownUntil = time + LINK_COOLDOWN;
          this.track(gsap.to(link, { opacity: 0, duration: 0.8 }));
        }
        return;
      }

      if (time < this._cooldownUntil) return;
      for (let a = 0; a < pos.length; a++) {
        for (let b = a + 1; b < pos.length; b++) {
          if (
            Math.hypot(pos[a].x - pos[b].x, pos[a].y - pos[b].y) < LINK_RANGE
          ) {
            this._pair = [a, b];
            this._pairEnd = time + LINK_HOLD;
            this.track(gsap.to(link, { opacity: 0.2, duration: 0.5 }));
            return;
          }
        }
      }
    },
  },
};
</script>
